/** Pure helpers for the payoff card: chart scaling, SVG paths and the
 * payoff-table row targets. Kept out of the component so the chart file
 * stays lean and the math is unit-testable. */

import type { PayoffLegView } from "@/lib/payoffApi";

export const PAYOFF_W = 640;
export const PAYOFF_H = 220;

export interface PayoffBounds {
  min: number;
  max: number;
}

/** Y bounds across every drawn series, always including zero (the P&L
 * axis anchors the chart), padded so lines never kiss the edge. */
export function pnlBounds(series: readonly (readonly number[])[]): PayoffBounds {
  const values = series.flatMap((s) => [...s, 0]);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = Math.max((max - min) * 0.08, 1);
  return { min: min - pad, max: max + pad };
}

export function xAt(spot: number, spots: readonly number[]): number {
  const first = spots[0] ?? 0;
  const last = spots[spots.length - 1] ?? 1;
  if (last === first) return 0;
  return ((spot - first) / (last - first)) * PAYOFF_W;
}

export function yAt(pnl: number, bounds: PayoffBounds): number {
  const span = bounds.max - bounds.min;
  return PAYOFF_H - ((pnl - bounds.min) / span) * PAYOFF_H;
}

export function linePath(
  spots: readonly number[],
  pnl: readonly number[],
  bounds: PayoffBounds,
): string {
  return pnl
    .map(
      (v, i) =>
        `${i === 0 ? "M" : "L"}${xAt(spots[i] ?? 0, spots).toFixed(1)} ${yAt(v, bounds).toFixed(1)}`,
    )
    .join(" ");
}

/** The line closed down to the zero axis — filled twice (green clipped
 * above zero, red clipped below) to shade profit/loss regions. */
export function areaPath(
  spots: readonly number[],
  pnl: readonly number[],
  bounds: PayoffBounds,
): string {
  const zero = yAt(0, bounds).toFixed(1);
  return `${linePath(spots, pnl, bounds)} L${PAYOFF_W.toFixed(1)} ${zero} L0 ${zero} Z`;
}

/** Clamped linear interpolation over the uniform ascending spot grid. */
export function pnlAt(spots: readonly number[], pnl: readonly number[], x: number): number {
  const n = spots.length;
  const first = spots[0] ?? 0;
  const last = spots[n - 1] ?? first;
  if (n < 2 || last <= first) return pnl[0] ?? 0;
  const clamped = Math.min(Math.max((x - first) / (last - first), 0), 1);
  const t = clamped * (n - 1);
  const i = Math.min(Math.floor(t), n - 2);
  const a = pnl[i] ?? 0;
  const b = pnl[i + 1] ?? 0;
  return a + (b - a) * (t - i);
}

/** EXACT on-expiry P&L at a target — the payoff has kinks at strikes, so
 * table rows must not interpolate the sampled grid across them (a round
 * target IS often a strike, and the smoothed value there reads wrong). */
export function expiryPnlAt(legs: readonly PayoffLegView[], spot: number): number {
  return legs.reduce((total, leg) => {
    const intrinsic =
      leg.optionType === "CE" ? Math.max(spot - leg.strike, 0) : Math.max(leg.strike - spot, 0);
    const direction = leg.side === "BUY" ? 1 : -1;
    return total + direction * (intrinsic - leg.entryFill) * leg.quantity;
  }, 0);
}

export function pnlTone(v: number): string {
  if (v > 0) return "text-success";
  if (v < 0) return "text-danger";
  return "";
}

export function formatMoney(v: number): string {
  const sign = v < 0 ? "-" : "";
  return `${sign}Rs ${Math.abs(Math.round(v)).toLocaleString("en-IN")}`;
}

export function formatLevel(v: number): string {
  return Math.round(v).toLocaleString("en-IN");
}

const TABLE_STEPS = [50, 100, 250, 500, 1000];

/** Round-number targets across the grid (~a dozen table rows). */
export function tableTargets(spots: readonly number[]): number[] {
  const first = spots[0];
  const last = spots[spots.length - 1];
  if (first === undefined || last === undefined || last <= first) return [];
  const raw = (last - first) / 12;
  const step = TABLE_STEPS.find((s) => s >= raw) ?? 1000;
  const out: number[] = [];
  for (let t = Math.ceil(first / step) * step; t <= last; t += step) out.push(t);
  return out;
}
